import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { 
  Bell, Mail, MessageSquare, Settings, CreditCard, History,
  Send, Phone, AlertCircle, CheckCircle, Clock, Truck, Package,
  Home, DollarSign, Users, Sparkles, Plus, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

// Notification type categories and labels
const NOTIFICATION_CATEGORIES = {
  "Customer Journey": [
    { type: "customer_welcome", label: "Customer Welcome (Login Code)", icon: Users },
    { type: "job_created", label: "Job Created", icon: Package },
    { type: "quote_ready", label: "Quote Ready", icon: DollarSign },
    { type: "quote_accepted", label: "Quote Accepted", icon: CheckCircle },
    { type: "quote_rejected", label: "Quote Rejected", icon: AlertCircle },
    { type: "job_confirmed", label: "Job Confirmed", icon: CheckCircle },
  ],
  "Move Day Operations": [
    { type: "move_reminder_week", label: "Reminder (1 Week Before)", icon: Clock },
    { type: "move_reminder_3day", label: "Reminder (3 Days Before)", icon: Clock },
    { type: "move_reminder_1day", label: "Reminder (1 Day Before)", icon: Clock },
    { type: "crew_on_way", label: "Crew On The Way", icon: Truck },
    { type: "crew_arrived", label: "Crew Arrived", icon: Home },
    { type: "loading_started", label: "Loading Started", icon: Package },
    { type: "loading_complete", label: "Loading Complete", icon: CheckCircle },
    { type: "in_transit", label: "In Transit", icon: Truck },
    { type: "delivery_delayed", label: "Delivery Delayed", icon: AlertCircle },
    { type: "arrived_destination", label: "Arrived at Destination", icon: Home },
    { type: "unloading_started", label: "Unloading Started", icon: Package },
    { type: "unloading_complete", label: "Unloading Complete", icon: CheckCircle },
    { type: "delivery_complete", label: "Delivery Complete (Signature)", icon: CheckCircle },
  ],
  "Storage Events": [
    { type: "items_in_storage", label: "Items In Storage", icon: Package },
    { type: "storage_reminder", label: "Storage Reminder (Monthly)", icon: Clock },
    { type: "items_leaving_storage", label: "Items Leaving Storage", icon: Truck },
  ],
  "Inventory Tracking": [
    { type: "item_loaded", label: "Item Loaded", icon: Package },
    { type: "item_unloaded", label: "Item Unloaded", icon: Package },
    { type: "fragile_item_alert", label: "Fragile Item Alert", icon: AlertCircle },
    { type: "missing_item_alert", label: "Missing Item Alert", icon: AlertCircle },
  ],
  "Payment": [
    { type: "payment_due", label: "Payment Due", icon: DollarSign },
    { type: "payment_reminder", label: "Payment Reminder", icon: Clock },
    { type: "payment_received", label: "Payment Received", icon: CheckCircle },
    { type: "invoice_sent", label: "Invoice Sent", icon: Mail },
  ],
};

const NotificationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [smsCredits, setSmsCredits] = useState(null);
  const [smsPricing, setSmsPricing] = useState(null);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Global settings
  const [globalSmsEnabled, setGlobalSmsEnabled] = useState(true);
  const [globalEmailEnabled, setGlobalEmailEnabled] = useState(true);
  const [reminderWeek, setReminderWeek] = useState(true);
  const [reminder3Day, setReminder3Day] = useState(true);
  const [reminder1Day, setReminder1Day] = useState(true);
  
  // Purchase dialog
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [purchaseBatches, setPurchaseBatches] = useState(1);
  
  // Manual send dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendType, setSendType] = useState('sms'); // sms or email
  const [sendRecipient, setSendRecipient] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, creditsRes, pricingRes, logsRes] = await Promise.all([
        api.get('/notifications/settings'),
        api.get('/notifications/sms/credits'),
        api.get('/notifications/sms/pricing'),
        api.get('/notifications/logs?limit=20'),
      ]);
      
      setSettings(settingsRes.data);
      setSmsCredits(creditsRes.data);
      setSmsPricing(pricingRes.data);
      setNotificationLogs(logsRes.data.logs || []);
      
      // Set global toggles from settings
      setGlobalSmsEnabled(settingsRes.data.sms_enabled ?? true);
      setGlobalEmailEnabled(settingsRes.data.email_enabled ?? true);
      setReminderWeek(settingsRes.data.reminder_frequency?.week_before ?? true);
      setReminder3Day(settingsRes.data.reminder_frequency?.['3_days_before'] ?? true);
      setReminder1Day(settingsRes.data.reminder_frequency?.['1_day_before'] ?? true);
    } catch (error) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalSettings = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/settings/global', null, {
        params: {
          sms_enabled: globalSmsEnabled,
          email_enabled: globalEmailEnabled,
          reminder_week: reminderWeek,
          reminder_3day: reminder3Day,
          reminder_1day: reminder1Day,
        }
      });
      toast.success('Global settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateNotificationSetting = async (notificationType, field, value) => {
    const currentSetting = settings?.notification_settings?.[notificationType] || 
                          settings?.default_settings?.[notificationType] || {};
    
    const update = {
      notification_type: notificationType,
      channel: field === 'channel' ? value : (currentSetting.channel || 'email'),
      enabled: field === 'enabled' ? value : (currentSetting.enabled ?? true),
      sms_enabled: field === 'sms_enabled' ? value : (currentSetting.sms_enabled ?? false),
    };
    
    try {
      await api.put('/notifications/settings', [update]);
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        notification_settings: {
          ...prev.notification_settings,
          [notificationType]: update
        }
      }));
      
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const purchaseCredits = async () => {
    try {
      const result = await api.post('/notifications/sms/purchase', {
        batch_count: purchaseBatches
      });
      toast.success(`Added ${purchaseBatches * 50} SMS credits!`);
      setShowPurchaseDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Purchase failed');
    }
  };

  const sendManualNotification = async () => {
    if (!sendRecipient || !sendMessage) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      if (sendType === 'sms') {
        await api.post('/notifications/sms/send', {
          recipient_phone: sendRecipient,
          message: sendMessage
        });
      } else {
        await api.post('/notifications/email/send', {
          recipient_email: sendRecipient,
          subject: sendSubject,
          message: sendMessage
        });
      }
      toast.success(`${sendType === 'sms' ? 'SMS' : 'Email'} sent successfully`);
      setShowSendDialog(false);
      setSendRecipient('');
      setSendSubject('');
      setSendMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send');
    }
  };

  const getNotificationSetting = (type) => {
    return settings?.notification_settings?.[type] || settings?.default_settings?.[type] || {};
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Notification Settings</h1>
          <p className="text-gray-600">Manage how you communicate with customers</p>
        </div>
        <Button onClick={() => setShowSendDialog(true)} data-testid="send-manual-btn">
          <Send size={16} className="mr-2" />
          Send Manual Message
        </Button>
      </div>

      {/* SMS Credits Overview */}
      {smsCredits?.warnings?.length > 0 && smsCredits.warnings.some(w => w.level !== 'info') && (
        <Card className={`border-l-4 ${
          smsCredits.warnings[0].level === 'critical' ? 'border-l-red-500 bg-red-50' :
          smsCredits.warnings[0].level === 'urgent' ? 'border-l-orange-500 bg-orange-50' :
          'border-l-yellow-500 bg-yellow-50'
        }`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <AlertCircle className={
                smsCredits.warnings[0].level === 'critical' ? 'text-red-500' :
                smsCredits.warnings[0].level === 'urgent' ? 'text-orange-500' :
                'text-yellow-500'
              } size={24} />
              <div className="flex-1">
                <p className="font-medium">{smsCredits.warnings[0].message}</p>
              </div>
              <Button size="sm" onClick={() => setShowPurchaseDialog(true)}>
                Buy Credits
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        {/* Total Available */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <MessageSquare className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Available</p>
                <p className="text-2xl font-bold">{smsCredits?.total_available || 0}</p>
                <p className="text-xs text-gray-400">SMS credits</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Free Credits (White Label) */}
        <Card className={smsCredits?.free_sms_eligible ? '' : 'opacity-60'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${smsCredits?.free_sms_eligible ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Sparkles className={smsCredits?.free_sms_eligible ? 'text-blue-600' : 'text-gray-400'} size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Free Credits</p>
                {smsCredits?.free_sms_eligible ? (
                  <>
                    <p className="text-2xl font-bold">{smsCredits?.free_remaining || 0}<span className="text-base font-normal text-gray-400">/{smsCredits?.free_sms_max || 50}</span></p>
                    <p className="text-xs text-gray-400">
                      Resets {smsCredits?.month_reset_date ? new Date(smsCredits.month_reset_date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : 'next month'}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-medium text-gray-500">Not Available</p>
                    <p className="text-xs text-gray-400">White Label only</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Used This Month */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Send className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Used This Month</p>
                <p className="text-2xl font-bold">{smsCredits?.used_this_month || 0}</p>
                <p className="text-xs text-gray-400">
                  {smsCredits?.free_sms_eligible && smsCredits?.free_sms_used > 0 
                    ? `${smsCredits.free_sms_used} free used`
                    : 'SMS messages'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Buy More */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 hover:border-primary" onClick={() => setShowPurchaseDialog(true)}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Plus className="text-primary" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Buy More</p>
                <p className="text-lg font-bold">50 for £{smsPricing?.batches?.[0]?.total || '2.80'}</p>
                <p className="text-xs text-primary font-medium">Click to purchase</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plan Info Banner */}
      {!smsCredits?.free_sms_eligible && (
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="text-primary" size={20} />
                <div>
                  <p className="font-medium">Get 50 Free SMS Every Month</p>
                  <p className="text-sm text-gray-600">Upgrade to White Label plan to receive 50 free SMS credits monthly, plus custom branding and more.</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/control-panel/billing'}>
                View Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings size={16} className="mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-types">
            <Bell size={16} className="mr-2" />
            Notification Types
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History size={16} className="mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Global Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Global Settings</CardTitle>
              <CardDescription>Master controls for all notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Channels</h3>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="text-blue-500" size={20} />
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-sm text-gray-500">Unlimited for all plans</p>
                      </div>
                    </div>
                    <Switch 
                      checked={globalEmailEnabled} 
                      onCheckedChange={setGlobalEmailEnabled}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="text-green-500" size={20} />
                      <div>
                        <p className="font-medium">SMS Notifications</p>
                        <p className="text-sm text-gray-500">White Label only (50 free/month)</p>
                      </div>
                    </div>
                    <Switch 
                      checked={globalSmsEnabled} 
                      onCheckedChange={setGlobalSmsEnabled}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold">Move Reminders</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>1 Week Before Move</Label>
                      <Switch checked={reminderWeek} onCheckedChange={setReminderWeek} />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>3 Days Before Move</Label>
                      <Switch checked={reminder3Day} onCheckedChange={setReminder3Day} />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>1 Day Before Move</Label>
                      <Switch checked={reminder1Day} onCheckedChange={setReminder1Day} />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={saveGlobalSettings} disabled={saving}>
                {saving ? 'Saving...' : 'Save Global Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Types Tab */}
        <TabsContent value="types">
          <ScrollArea className="h-[600px]">
            <div className="space-y-6 pr-4">
              {Object.entries(NOTIFICATION_CATEGORIES).map(([category, notifications]) => (
                <Card key={category}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {notifications.map(({ type, label, icon: Icon }) => {
                        const setting = getNotificationSetting(type);
                        return (
                          <div 
                            key={type} 
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-3">
                              <Icon size={18} className="text-gray-500" />
                              <span className="text-sm font-medium">{label}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Mail size={14} className={setting.channel !== 'none' ? 'text-blue-500' : 'text-gray-300'} />
                                <Switch
                                  checked={setting.enabled !== false}
                                  onCheckedChange={(val) => updateNotificationSetting(type, 'enabled', val)}
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <MessageSquare size={14} className={setting.sms_enabled ? 'text-green-500' : 'text-gray-300'} />
                                <Switch
                                  checked={setting.sms_enabled === true}
                                  onCheckedChange={(val) => updateNotificationSetting(type, 'sms_enabled', val)}
                                  disabled={!globalSmsEnabled}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Recent Notifications</CardTitle>
              <CardDescription>Last 20 notifications sent</CardDescription>
            </CardHeader>
            <CardContent>
              {notificationLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No notifications sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notificationLogs.map((log, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 border rounded-lg">
                      {log.channel === 'sms' ? (
                        <MessageSquare className="text-green-500" size={20} />
                      ) : (
                        <Mail className="text-blue-500" size={20} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {log.recipient_phone || log.recipient_email}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{log.message}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant={log.status === 'sent' ? 'success' : 'destructive'}>
                          {log.status}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(log.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase SMS Credits</DialogTitle>
            <DialogDescription>
              Buy SMS credits in batches of 50
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPurchaseBatches(Math.max(1, purchaseBatches - 1))}
              >
                <Minus size={16} />
              </Button>
              <div className="text-center">
                <p className="text-3xl font-bold">{purchaseBatches * 50}</p>
                <p className="text-sm text-gray-500">SMS Credits</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPurchaseBatches(purchaseBatches + 1)}
              >
                <Plus size={16} />
              </Button>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-primary">
                £{(smsPricing?.batches?.[0]?.total * purchaseBatches || 2.80 * purchaseBatches).toFixed(2)}
              </p>
              <p className="text-sm text-gray-500">Total price</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseDialog(false)}>
              Cancel
            </Button>
            <Button onClick={purchaseCredits}>
              <CreditCard size={16} className="mr-2" />
              Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Manual Message</DialogTitle>
            <DialogDescription>
              Send a custom SMS or email to a customer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button
                variant={sendType === 'sms' ? 'default' : 'outline'}
                onClick={() => setSendType('sms')}
                className="flex-1"
              >
                <MessageSquare size={16} className="mr-2" />
                SMS
              </Button>
              <Button
                variant={sendType === 'email' ? 'default' : 'outline'}
                onClick={() => setSendType('email')}
                className="flex-1"
              >
                <Mail size={16} className="mr-2" />
                Email
              </Button>
            </div>
            
            <div>
              <Label>{sendType === 'sms' ? 'Phone Number' : 'Email Address'}</Label>
              <Input
                placeholder={sendType === 'sms' ? '+44 7700 900000' : 'customer@email.com'}
                value={sendRecipient}
                onChange={(e) => setSendRecipient(e.target.value)}
              />
            </div>
            
            {sendType === 'email' && (
              <div>
                <Label>Subject</Label>
                <Input
                  placeholder="Message from your moving company"
                  value={sendSubject}
                  onChange={(e) => setSendSubject(e.target.value)}
                />
              </div>
            )}
            
            <div>
              <Label>Message</Label>
              <Textarea
                placeholder="Your message..."
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                rows={4}
              />
              {sendType === 'sms' && (
                <p className="text-xs text-gray-500 mt-1">
                  {sendMessage.length}/160 characters ({Math.ceil(sendMessage.length / 160) || 1} SMS)
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancel
            </Button>
            <Button onClick={sendManualNotification}>
              <Send size={16} className="mr-2" />
              Send {sendType === 'sms' ? 'SMS' : 'Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettings;
